// src/theme.ts — theme factory (light + dark)
import { alpha, createTheme, type ThemeOptions } from "@mui/material/styles";
import {
  palette,
  plasmaPalette,
  semanticPalette,
  shape,
  typographyTokens,
  lightShadows,
  darkShadows,
  glass,
  appChrome,
  brandPlasmaAmbientBackground,
  darkGradientBackground,
  surfacePlasmaHoverCss,
  uiLayers,
} from "./theme/tokens";

// Re-export for backward compat
export { glass } from "./theme/tokens";

export const mono13 = {
  fontFamily: typographyTokens.monoFamily,
  fontSize: 13,
  letterSpacing: 0,
  fontVariantNumeric: "tabular-nums" as const,
};

function getDesignTokens(mode: "light" | "dark"): ThemeOptions {
  const isLight = mode === "light";
  const semantic = isLight ? semanticPalette.light : semanticPalette.dark;
  const panelBorder = isLight ? palette.light.border : palette.dark.border;
  const panelBorderHover = isLight ? palette.light.borderHover : palette.dark.borderHover;
  const panelBg = isLight ? palette.light.paper : palette.dark.surfaceElevated;
  const panelBgRaised = isLight ? palette.light.surface : palette.dark.surfaceRaised;
  const panelBgHover = isLight ? alpha(plasmaPalette.violetSurface, 0.018) : palette.dark.surfaceRaised;
  const subtleBg = isLight ? alpha(plasmaPalette.violetSurface, 0.035) : alpha(palette.dark.surfaceRaised, 0.38);
  const subtleBgHover = isLight ? alpha(plasmaPalette.violetSurface, 0.055) : alpha(palette.dark.surfaceRaised, 0.56);
  const controlBorder = isLight ? alpha(plasmaPalette.violetSurface, 0.07) : alpha(palette.primary.main, 0.085);
  const controlBorderHover = isLight ? alpha(plasmaPalette.violetSurface, 0.12) : alpha(palette.primary.main, 0.13);
  const controlBorderActive = isLight ? alpha(plasmaPalette.violetSurface, 0.16) : alpha(palette.primary.main, 0.18);
  const controlFill = isLight ? alpha(palette.light.paper, 0.2) : alpha(palette.dark.surfaceElevated, 0.055);
  const controlFillHover = isLight ? alpha(palette.light.paper, 0.42) : alpha(palette.dark.surfaceElevated, 0.13);
  const controlFillActive = isLight ? alpha(palette.primary.main, 0.16) : alpha(palette.primary.main, 0.095);
  const controlText = isLight ? alpha(palette.text.lightPrimary, 0.74) : alpha(palette.text.darkSecondary, 0.76);
  const controlTextActive = isLight ? alpha(palette.text.lightPrimary, 0.9) : alpha(palette.text.darkPrimary, 0.92);
  const controlFocusRing = alpha(palette.primary.main, isLight ? 0.2 : 0.16);
  const overlayBg = isLight ? alpha(palette.light.paper, 0.88) : alpha(palette.dark.surface, 0.84);
  const overlayBorder = isLight ? alpha(plasmaPalette.violetSurface, 0.11) : palette.dark.border;
  const overlayBgImage = [
    isLight
      ? "linear-gradient(180deg, rgba(255,255,255,.72) 0%, rgba(251,249,255,.9) 100%)"
      : "linear-gradient(180deg, rgba(20,28,40,.44) 0%, rgba(12,18,27,.68) 100%)",
    brandPlasmaAmbientBackground(mode),
  ].join(", ");
  const overlayBackdrop = isLight ? "rgba(15,23,42,.28)" : "rgba(7,10,16,.58)";
  const overlayLabelBg = isLight ? alpha(palette.light.paper, 0.86) : alpha(palette.dark.surface, 0.82);
  const overlayShadow = isLight
    ? "0 10px 28px rgba(15,23,42,0.1), inset 0 1px 0 rgba(255,255,255,0.72)"
    : "0 14px 38px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.045)";
  const weights = typographyTokens.weights;
  const darkPrimary = {
    main: palette.primary.main,
    dark: palette.primary.dark,
    light: palette.primary.light,
    contrastText: palette.primary.contrastText,
  };

  return {
    zIndex: { appBar: uiLayers.chrome, drawer: uiLayers.drawer, modal: uiLayers.modal, snackbar: uiLayers.snackbar, tooltip: uiLayers.tooltip },
    palette: {
      mode,
      primary: isLight ? palette.primary : darkPrimary,
      info: { main: semantic.info },
      success: { main: semantic.success },
      warning: { main: semantic.warning },
      error: { main: semantic.error },
      background: isLight
        ? { default: palette.light.background, paper: palette.light.paper }
        : { default: palette.dark.base, paper: palette.dark.surface },
      text: isLight
        ? { primary: palette.text.lightPrimary, secondary: palette.text.lightSecondary }
        : { primary: palette.text.darkPrimary, secondary: palette.text.darkSecondary },
      divider: isLight
        ? palette.light.border
        : "rgba(216,182,255,0.085)",
    },
    shape: { borderRadius: shape.borderRadiusSmall },
    shadows: (isLight ? [...lightShadows] : [...darkShadows]) as any,
    typography: {
      fontFamily: typographyTokens.fontFamily,
      fontSize: typographyTokens.baseFontSize,
      h1: { fontWeight: weights.bold, letterSpacing: 0, lineHeight: 1.06 },
      h2: { fontWeight: weights.bold, letterSpacing: 0, lineHeight: 1.08 },
      h3: { fontWeight: weights.bold, letterSpacing: 0, lineHeight: 1.1 },
      h4: { fontWeight: weights.bold, letterSpacing: 0, lineHeight: 1.12 },
      h5: { fontWeight: weights.bold, letterSpacing: 0, lineHeight: 1.15 },
      h6: { fontWeight: weights.semibold, fontSize: 16, letterSpacing: 0, lineHeight: 1.18 },
      button: { fontWeight: weights.semibold, fontSize: 13, letterSpacing: 0 },
      body1: { fontWeight: weights.body, fontSize: 13.5, lineHeight: 1.5 },
      body2: { fontWeight: weights.body, fontSize: 12.5, lineHeight: 1.45 },
      subtitle1: { fontWeight: weights.semibold, fontSize: 14.5, letterSpacing: 0 },
      subtitle2: { fontWeight: weights.semibold, fontSize: 13, letterSpacing: 0 },
      caption: { fontWeight: weights.medium, fontSize: 11.5, letterSpacing: 0 },
      overline: { fontWeight: weights.semibold, fontSize: 11, letterSpacing: 0, textTransform: "none" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@font-face": [
            {
              fontFamily: "WH UI",
              src: `url("/fonts/NotoSans-Regular.ttf") format("truetype")`,
              fontWeight: 400,
              fontStyle: "normal",
              fontDisplay: "swap",
            },
            {
              fontFamily: "WH UI",
              src: `url("/fonts/NotoSans-Bold.ttf") format("truetype")`,
              fontWeight: 700,
              fontStyle: "normal",
              fontDisplay: "swap",
            },
          ],
          "html, body, #root": { minHeight: "100%" },
          body: {
            fontFamily: typographyTokens.fontFamily,
            fontWeight: weights.body,
            backgroundImage: isLight ? "none" : darkGradientBackground,
            backgroundColor: isLight ? palette.light.background : palette.dark.base,
            backgroundAttachment: "fixed",
            fontFeatureSettings: '"kern", "liga", "tnum"',
            textRendering: "optimizeLegibility",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "button, input, textarea, select": {
            fontFamily: typographyTokens.fontFamily,
            fontWeight: weights.body,
          },
          "code, pre, kbd, samp": {
            fontFamily: typographyTokens.monoFamily,
            fontFeatureSettings: '"kern", "tnum"',
          },
          "*": {
            scrollbarColor: isLight
              ? "rgba(15,23,42,.28) transparent"
              : "rgba(153,168,184,.32) transparent",
          },
          "::selection": {
            background: isLight ? alpha(palette.primary.dark, 0.18) : alpha(palette.primary.main, 0.24),
          },
          "@keyframes wh-surface-plasma-drift": {
            "0%": {
              backgroundPosition: "12% 0%, 88% 8%, 48% 100%, 0% 0%",
            },
            "100%": {
              backgroundPosition: "24% 8%, 76% 18%, 54% 86%, 0% 0%",
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily,
            letterSpacing: 0,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSmall,
            border: "1px solid",
            ...(isLight
              ? {
                  backgroundColor: palette.light.paper,
                  borderColor: panelBorder,
                  boxShadow: "0 1px 2px rgba(15,23,42,.04)",
                }
              : {
                  backgroundColor: panelBg,
                  borderColor: panelBorder,
                  boxShadow: "0 1px 0 rgba(255,255,255,.026), 0 8px 20px rgba(0,0,0,.14)",
                }),
            transition: "border-color .15s ease, background-color .15s ease",
            ...surfacePlasmaHoverCss(mode, { activeOpacity: isLight ? 0.16 : 0.2 }),
            "&:hover": {
              borderColor: panelBorderHover,
              backgroundColor: panelBgHover,
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: "14px 16px 10px",
            borderBottom: "1px solid",
            borderColor: panelBorder,
          },
          title: {
            fontSize: 15.5,
            fontWeight: weights.semibold,
            lineHeight: 1.18,
            letterSpacing: 0,
          },
          subheader: {
            marginTop: 3,
            fontSize: 12,
            lineHeight: 1.35,
            color: isLight ? palette.text.lightSecondary : palette.text.darkSecondary,
          },
          action: {
            marginTop: 0,
            marginRight: 0,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 16,
            "&:last-child": {
              paddingBottom: 16,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSmall,
            backgroundImage: "none",
            ...(isLight
              ? {}
              : {
                  backgroundColor: alpha(palette.dark.surfaceElevated, 0.94),
                  borderColor: panelBorder,
                }),
            "&.MuiPaper-outlined": {
              borderColor: panelBorder,
              transition: "border-color .15s ease, background-color .15s ease",
              ...surfacePlasmaHoverCss(mode, { activeOpacity: isLight ? 0.16 : 0.2 }),
              "&:hover": {
                borderColor: panelBorderHover,
                backgroundColor: panelBgHover,
              },
            },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          BackdropProps: {
            sx: {
              backdropFilter: "blur(8px) saturate(1.08)",
              WebkitBackdropFilter: "blur(8px) saturate(1.08)",
            },
          },
        },
        styleOverrides: {
          paper: {
            overflow: "hidden",
            isolation: "isolate",
            borderRadius: shape.borderRadiusSmall,
            border: "1px solid",
            borderColor: overlayBorder,
            backgroundColor: overlayBg,
            backgroundImage: overlayBgImage,
            backgroundBlendMode: "normal, soft-light",
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            boxShadow: overlayShadow,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            borderColor: overlayBorder,
            backgroundColor: overlayBg,
            backgroundImage: overlayBgImage,
            backgroundBlendMode: "normal, soft-light",
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            boxShadow: isLight ? "0 16px 42px rgba(15,23,42,.14)" : "0 18px 48px rgba(0,0,0,.34)",
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: overlayBackdrop,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSmall,
            color: controlTextActive,
            backgroundColor: controlFill,
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            transition: "background-color .16s ease, border-color .16s ease, color .16s ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: controlBorder,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: controlBorderHover,
            },
            "&:hover": {
              backgroundColor: controlFillHover,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: controlBorderActive,
              borderWidth: 1,
            },
            "&.Mui-focused": {
              backgroundColor: controlFillActive,
              boxShadow: `0 0 0 2px ${controlFocusRing}`,
            },
          },
          input: { paddingTop: 8, paddingBottom: 8 },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: 13.5,
            fontWeight: weights.body,
            letterSpacing: 0,
          },
          input: {
            fontWeight: weights.body,
            letterSpacing: 0,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: weights.medium,
            letterSpacing: 0,
            color: isLight ? alpha(palette.text.lightPrimary, 0.58) : alpha(palette.text.darkSecondary, 0.56),
            "&.Mui-focused": {
              color: controlTextActive,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginRight: 0,
            fontSize: 11.5,
            lineHeight: 1.3,
            color: isLight ? alpha(palette.text.lightPrimary, 0.54) : alpha(palette.text.darkSecondary, 0.58),
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: controlText,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusMedium,
            height: 24,
            fontWeight: weights.medium,
            fontSize: 11.5,
            letterSpacing: 0,
            borderColor: panelBorder,
            ...(isLight ? {} : { backgroundColor: panelBgRaised }),
          },
          label: {
            paddingLeft: 8,
            paddingRight: 8,
          },
          iconSmall: {
            marginLeft: 6,
          },
          colorDefault: {
            ...(isLight
              ? {}
              : {
                  color: palette.text.darkSecondary,
                  borderColor: panelBorder,
                }),
          },
          colorSuccess: {
            backgroundColor: alpha(semantic.success, isLight ? 0.08 : 0.12),
            color: semantic.success,
          },
          colorWarning: {
            backgroundColor: alpha(semantic.warning, isLight ? 0.08 : 0.12),
            color: semantic.warning,
          },
          colorError: {
            backgroundColor: alpha(semantic.error, isLight ? 0.08 : 0.12),
            color: semantic.error,
          },
          colorInfo: {
            backgroundColor: alpha(semantic.info, isLight ? 0.08 : 0.12),
            color: semantic.info,
          },
          colorPrimary: {
            backgroundColor: alpha(palette.primary.main, isLight ? 0.1 : 0.12),
            color: isLight ? palette.primary.dark : palette.primary.main,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none" as const,
            borderRadius: shape.borderRadiusSmall,
            fontWeight: weights.semibold,
            letterSpacing: 0,
            minHeight: 36,
            transition: "background-color .16s ease, border-color .16s ease, color .16s ease",
            "&:focus-visible": {
              outline: "none",
              boxShadow: `0 0 0 2px ${controlFocusRing}`,
            },
          },
          sizeSmall: {
            minHeight: 32,
            padding: "5px 10px",
            fontSize: 12.5,
          },
          containedPrimary: {
            color: controlTextActive,
            background: controlFillActive,
            border: `1px solid ${controlBorderActive}`,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.045)",
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            "&:hover": {
              background: controlFillHover,
              borderColor: controlBorderHover,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.045)",
            },
            "&.Mui-disabled": {
              border: `1px solid ${controlBorder}`,
              background: isLight ? alpha(palette.light.paper, 0.12) : alpha(palette.dark.surfaceElevated, 0.075),
            },
          },
          outlined: {
            borderColor: controlBorder,
            backgroundColor: controlFill,
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            "&:hover": {
              borderColor: controlBorderHover,
              backgroundColor: controlFillHover,
            },
            "&.Mui-disabled": {
              borderColor: controlBorder,
            },
          },
          text: {
            "&:hover": {
              backgroundColor: controlFillHover,
            },
          },
          outlinedError: {
            color: semantic.error,
            borderColor: alpha(semantic.error, isLight ? 0.22 : 0.24),
            backgroundColor: alpha(semantic.error, isLight ? 0.035 : 0.055),
            "&:hover": {
              borderColor: alpha(semantic.error, isLight ? 0.34 : 0.36),
              backgroundColor: alpha(semantic.error, isLight ? 0.055 : 0.08),
            },
          },
        },
        defaultProps: { disableElevation: true },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSmall,
            color: controlText,
            border: `1px solid ${controlBorder}`,
            backgroundColor: controlFill,
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            transition: "background-color .16s ease, border-color .16s ease, color .16s ease",
            "&:hover": {
              color: controlTextActive,
              borderColor: controlBorderHover,
              backgroundColor: controlFillHover,
            },
            "&:focus-visible": {
              outline: "none",
              boxShadow: `0 0 0 2px ${controlFocusRing}`,
            },
            "&.Mui-disabled": {
              borderColor: controlBorder,
              backgroundColor: isLight ? alpha(palette.light.paper, 0.08) : alpha(palette.dark.surfaceElevated, 0.04),
            },
          },
          sizeSmall: {
            width: 34,
            height: 34,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isLight
              ? palette.light.border
              : panelBorder,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSmall,
            backgroundImage: "none",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: isLight ? palette.light.border : panelBorder,
            padding: "8px 12px",
            fontSize: 13,
          },
          head: {
            backgroundColor: subtleBg,
            color: isLight ? palette.text.lightSecondary : palette.text.darkSecondary,
            fontSize: 11,
            fontWeight: weights.semibold,
            lineHeight: 1.2,
            textTransform: "none",
            letterSpacing: 0,
            whiteSpace: "nowrap",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background-color .12s ease",
            "&:last-of-type .MuiTableCell-root": {
              borderBottomColor: "transparent",
            },
            "&:hover": {
              backgroundColor: subtleBgHover,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            color: isLight ? palette.text.lightSecondary : palette.text.darkSecondary,
            fontSize: 12.5,
          },
          toolbar: {
            minHeight: 42,
            paddingLeft: 12,
            paddingRight: 8,
          },
          selectLabel: {
            fontSize: 12,
          },
          displayedRows: {
            fontSize: 12,
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          density: "compact",
        },
        styleOverrides: {
          root: {
            border: 0,
            fontSize: 13,
            '&& .MuiTablePagination-input': { display: 'inline-flex', marginLeft: 1, marginRight: 1 },
            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5 },
            '& .MuiDataGrid-footerContainer': { flexWrap: 'wrap' },
            backgroundImage: "none",
            fontFeatureSettings: '"tnum"',
            color: isLight ? palette.text.lightPrimary : palette.text.darkPrimary,
            "--DataGrid-rowBorderColor": isLight
              ? palette.light.border
              : panelBorder,
            "& .MuiDataGrid-columnHeaders": {
              borderBottomColor: isLight ? palette.light.border : panelBorder,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: isLight
                ? alpha(plasmaPalette.violetSurface, 0.035)
                : subtleBgHover,
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: alpha(palette.primary.main, isLight ? 0.12 : 0.075),
            },
            "& .MuiDataGrid-columnSeparator": {
              opacity: 0,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-root": {
              minHeight: 30,
              paddingLeft: 8,
              paddingRight: 8,
              color: controlText,
              borderRadius: shape.borderRadiusSmall,
              "&:hover": {
                color: controlTextActive,
                backgroundColor: controlFillHover,
              },
            },
            "& .MuiDataGrid-toolbarContainer .MuiFormControl-root": {
              minWidth: 220,
            },
          },
          columnHeader: {
            backgroundColor: isLight
              ? alpha(plasmaPalette.violetSurface, 0.035)
              : subtleBg,
            color: isLight ? palette.text.lightSecondary : palette.text.darkSecondary,
            fontWeight: weights.semibold,
            textTransform: "none",
            letterSpacing: 0,
            fontSize: 11,
          },
          cell: {
            outline: "none !important",
          },
          toolbarContainer: {
            padding: 0,
            borderBottom: "1px solid",
            borderColor: isLight
              ? palette.light.border
              : panelBorder,
          },
          footerContainer: {
            minHeight: 42,
            borderTopColor: isLight
              ? palette.light.border
              : panelBorder,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 38,
          },
          indicator: {
            height: 2,
            borderRadius: 2,
            backgroundColor: isLight ? palette.primary.main : palette.accent.violet,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 38,
            textTransform: "none",
            fontSize: 12.5,
            fontWeight: weights.semibold,
            color: isLight ? palette.text.lightSecondary : palette.text.darkSecondary,
            "&.Mui-selected": {
              color: isLight ? palette.text.lightPrimary : palette.text.darkPrimary,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            minHeight: 36,
            borderRadius: shape.borderRadiusSmall,
            borderColor: controlBorder,
            color: controlText,
            backgroundColor: controlFill,
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            textTransform: "none",
            fontSize: 12.5,
            fontWeight: weights.semibold,
            letterSpacing: 0,
            padding: "6px 10px",
            transition: "background-color .16s ease, border-color .16s ease, color .16s ease",
            "&:hover": {
              color: controlTextActive,
              borderColor: controlBorderHover,
              backgroundColor: controlFillHover,
            },
            "&:focus-visible": {
              outline: "none",
              boxShadow: `0 0 0 2px ${controlFocusRing}`,
            },
            "&.Mui-selected": {
              color: isLight ? palette.text.lightPrimary : palette.text.darkPrimary,
              backgroundColor: alpha(palette.primary.main, isLight ? 0.22 : 0.16),
              borderColor: alpha(palette.primary.main, isLight ? 0.34 : 0.32),
              boxShadow: [
                "inset 0 1px 0 rgba(255,255,255,0.055)",
                `inset 0 0 0 1px ${alpha(palette.primary.main, isLight ? 0.08 : 0.07)}`,
              ].join(", "),
              "&:hover": {
                backgroundColor: alpha(palette.primary.main, isLight ? 0.27 : 0.2),
                borderColor: alpha(palette.primary.main, isLight ? 0.42 : 0.4),
              },
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            width: "fit-content",
            maxWidth: "100%",
            padding: 4,
            gap: 4,
            border: "1px solid",
            borderColor: controlBorder,
            borderRadius: shape.borderRadiusSmall,
            backgroundColor: isLight ? alpha(palette.light.paper, 0.34) : alpha(palette.dark.surface, 0.38),
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            boxShadow: isLight
              ? "0 8px 20px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.58)"
              : "0 10px 24px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
          },
          grouped: {
            margin: "0 !important",
            border: `1px solid ${controlBorder}`,
            borderRadius: `${shape.borderRadiusSmall}px !important`,
            "&:not(:first-of-type)": {
              borderLeft: `1px solid ${controlBorder}`,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "16px 22px 14px",
            fontSize: 14.5,
            fontWeight: weights.bold,
            lineHeight: 1.18,
            borderBottom: "1px solid",
            borderColor: panelBorder,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "18px 22px",
            borderColor: panelBorder,
            "& .MuiFormControl-root, & .MuiTextField-root": {
              minWidth: 0,
            },
            "& .MuiOutlinedInput-root": {
              minHeight: 42,
            },
            "& .MuiOutlinedInput-input": {
              paddingTop: 10,
              paddingBottom: 10,
            },
            "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
              alignItems: "flex-start",
              minHeight: 96,
              padding: "10px 12px",
            },
            "& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiInputBase-input": {
              padding: 0,
            },
            "& .MuiInputLabel-outlined": {
              maxWidth: "calc(100% - 28px)",
            },
            "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
              maxWidth: "calc(133% - 30px)",
              paddingLeft: 4,
              paddingRight: 4,
              borderRadius: 4,
              backgroundColor: overlayLabelBg,
              transform: "translate(10px, -7px) scale(0.78)",
            },
            "&.MuiDialogContent-dividers": {
              borderTop: 0,
              borderBottom: 0,
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "14px 22px",
            gap: 10,
            borderTop: "1px solid",
            borderColor: panelBorder,
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            overflow: "hidden",
            border: "1px solid",
            borderColor: overlayBorder,
            backgroundColor: overlayBg,
            backgroundImage: overlayBgImage,
            backgroundBlendMode: "normal, soft-light",
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            boxShadow: isLight ? "0 12px 32px rgba(15,23,42,.12)" : "0 12px 34px rgba(0,0,0,.34)",
          },
          list: {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            overflow: "hidden",
            border: "1px solid",
            borderColor: overlayBorder,
            backgroundColor: overlayBg,
            backgroundImage: overlayBgImage,
            backgroundBlendMode: "normal, soft-light",
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
            boxShadow: isLight ? "0 12px 32px rgba(15,23,42,.12)" : "0 12px 34px rgba(0,0,0,.34)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 34,
            fontSize: 13,
            borderRadius: 5,
            margin: "2px 6px",
            "&:hover": {
              backgroundColor: subtleBgHover,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSmall,
            border: "1px solid",
            borderColor: panelBorder,
            backgroundImage: "none",
          },
          standardError: {
            backgroundColor: alpha(semantic.error, isLight ? 0.06 : 0.09),
          },
          standardWarning: {
            backgroundColor: alpha(semantic.warning, isLight ? 0.06 : 0.09),
          },
          standardSuccess: {
            backgroundColor: alpha(semantic.success, isLight ? 0.06 : 0.09),
          },
          standardInfo: {
            backgroundColor: alpha(semantic.info, isLight ? 0.06 : 0.09),
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 5,
            borderRadius: 5,
            backgroundColor: isLight ? palette.light.border : panelBorder,
          },
          bar: {
            borderRadius: 5,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: 12,
            ...(isLight
              ? {}
              : {
                  backgroundColor: palette.dark.surfaceElevated,
                  color: palette.text.darkPrimary,
                  border: `1px solid ${palette.dark.border}`,
                  boxShadow: "0 10px 26px rgba(0,0,0,.26)",
                }),
          },
        },
      },
    },
  };
}

export function createAppTheme(mode: "light" | "dark") {
  return createTheme(getDesignTokens(mode));
}

// Default export for backward compat
export const theme = createAppTheme("light");
